<template>
  <div class="faq-page">
    <APageHead title="자주 묻는 질문 (FAQ)" sub="사용자들이 자주 묻는 질문과 답변을 관리합니다.">
      <template #actions>
        <ABtn kind="primary" icon="add" @click="openCreateModal">FAQ 추가</ABtn>
      </template>
    </APageHead>

    <!-- KPI -->
    <div class="faq-page__stats">
      <ACard><AStat label="전체 FAQ" :value="faqs.length" unit="건" /></ACard>
      <ACard><AStat label="활성 FAQ" :value="activeFaqs" unit="건" /></ACard>
      <ACard><AStat label="카테고리" :value="categories.length" unit="개" /></ACard>
      <ACard><AStat label="총 조회수" :value="monthlyViews" unit="회" /></ACard>
    </div>

    <!-- 검색 + 필터 -->
    <ACard overflow="visible">
      <div class="faq-page__filters">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">search</i>
            검색
          </label>
          <input
            v-model="searchQuery"
            type="text"
            class="p-input"
            placeholder="질문이나 답변 검색..."
            @input="filterFaqs"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">category</i>
            카테고리
          </label>
          <select v-model="selectedCategory" class="p-input" @change="filterFaqs">
            <option value="">전체 카테고리</option>
            <option v-for="c in categories" :key="c.value" :value="c.value">
              {{ $t(c.labelKey) }}
            </option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">tune</i>
            상태
          </label>
          <select v-model="statusFilter" class="p-input" @change="filterFaqs">
            <option value="">전체 상태</option>
            <option value="active">활성</option>
            <option value="inactive">비활성</option>
          </select>
        </div>
      </div>
    </ACard>

    <!-- FAQ 리스트 -->
    <ACard>
      <template #title>
        <span class="faq-page__sec-title">
          <i class="material-icons-round faq-page__sec-icon">help_outline</i>
          FAQ 목록
        </span>
      </template>
      <template #action>
        <APill kind="info">{{ filteredFaqs.length }}건</APill>
      </template>

      <div v-if="loading" class="faq-page__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>FAQ를 불러오는 중...</p>
      </div>

      <AEmpty
        v-else-if="filteredFaqs.length === 0"
        title="검색 결과가 없어요"
        sub="필터 조건을 변경하거나 새 FAQ를 추가해보세요."
      />

      <div v-else class="faq-page__list">
        <article
          v-for="faq in paginatedFaqs"
          :key="faq.id"
          :class="['faq-page__item', { 'is-open': expandedFaqId === faq.id }]"
        >
          <button
            type="button"
            class="faq-page__toggle"
            :class="{ 'is-inactive': !faq.is_active }"
            @click="toggleAccordion(faq.id)"
          >
            <APill kind="yellow">{{ getCategoryLabel(faq.category) }}</APill>
            <span class="faq-page__question">{{ faq.question }}</span>
            <APill :kind="faq.is_active ? 'ok' : 'default'" dot>
              {{ faq.is_active ? '활성' : '비활성' }}
            </APill>
            <span class="faq-page__views">
              <i class="material-icons-round">visibility</i>{{ faq.views || 0 }}
            </span>
            <i class="material-icons-round faq-page__chev">
              {{ expandedFaqId === faq.id ? 'expand_less' : 'expand_more' }}
            </i>
          </button>

          <div v-show="expandedFaqId === faq.id" class="faq-page__body">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="faq-page__answer" v-html="faq.answer"></div>

            <div class="faq-page__meta">
              <span class="faq-page__updated">
                <i class="material-icons-round">schedule</i>
                최종 수정: {{ formatDate(faq.updated_at) }}
              </span>
              <div class="faq-page__actions">
                <ABtn size="sm" icon="edit" @click="openEditModal(faq)">수정</ABtn>
                <ABtn size="sm" kind="danger" icon="delete" @click="deleteFaq(faq.id)">삭제</ABtn>
              </div>
            </div>
          </div>
        </article>
      </div>

      <APagination
        v-if="filteredFaqs.length > itemsPerPage"
        :current-page="currentPage"
        :total="filteredFaqs.length"
        :page-size="itemsPerPage"
        @change="(p) => currentPage = p"
      />
    </ACard>

    <!-- 생성/수정 모달 -->
    <AModal v-model="showModal" size="lg">
      <template #title>
        <span class="faq-page__modal-title">
          <i class="material-icons-round faq-page__modal-icon">
            {{ editingFaq ? 'edit_note' : 'help_center' }}
          </i>
          {{ editingFaq ? 'FAQ 수정' : '새로운 FAQ 추가' }}
        </span>
      </template>
      <template #sub>
        {{ editingFaq ? '기존 FAQ 정보를 수정합니다.' : '사용자들이 자주 묻는 질문을 등록합니다.' }}
      </template>

      <form class="faq-page__form" @submit.prevent="saveFaq">
        <div class="faq-page__form-grid">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">category</i>
              카테고리 <span class="p-field__req">*</span>
            </label>
            <select v-model="formData.category" class="p-input" required>
              <option value="">카테고리를 선택하세요</option>
              <option v-for="c in categories" :key="c.value" :value="c.value">
                {{ $t(c.labelKey) }}
              </option>
            </select>
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">sort</i>
              정렬 순서
            </label>
            <input v-model.number="formData.sort_order" type="number" class="p-input" min="0" placeholder="0" />
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">quiz</i>
            질문 <span class="p-field__req">*</span>
          </label>
          <input
            v-model="formData.question"
            type="text"
            class="p-input"
            placeholder="예: 비밀번호를 잊어버렸어요. 어떻게 재설정하나요?"
            required
          />
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">chat</i>
            답변 <span class="p-field__req">*</span>
          </label>
          <textarea
            v-model="formData.answer"
            class="p-input p-input--area"
            rows="8"
            placeholder="상세한 답변을 작성하세요."
            required
          ></textarea>
          <div class="p-field__hint">
            <i>info</i>마크다운 또는 HTML 형식을 지원합니다.
          </div>
        </div>

        <div class="faq-page__switch">
          <div>
            <div class="faq-page__switch-label">
              <i class="material-icons-round">visibility</i>
              FAQ 공개 상태
            </div>
            <div class="faq-page__switch-sub">활성화하면 사용자에게 즉시 표시됩니다.</div>
          </div>
          <button
            type="button"
            :class="['p-switch', { 'is-on': formData.is_active }]"
            @click="formData.is_active = !formData.is_active"
          ></button>
        </div>
      </form>

      <template #foot>
        <ABtn @click="showModal = false">취소</ABtn>
        <ABtn kind="primary" :icon="editingFaq ? 'save' : 'add'" @click="saveFaq">
          {{ editingFaq ? '수정 완료' : 'FAQ 등록' }}
        </ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import AModal from "@/components/Pineapple/AModal.vue";

export default {
  name: "HelpDeskFAQs",
  components: { APageHead, ABtn, ACard, AStat, APill, AEmpty, APagination, AModal },
  data() {
    return {
      faqs: [],
      filteredFaqs: [],
      categories: [
        { value: "general", labelKey: "helpdesk_category.general" },
        { value: "payment", labelKey: "helpdesk_category.payment" },
        { value: "technical", labelKey: "helpdesk_category.technical" },
        { value: "account", labelKey: "helpdesk_category.account" },
        { value: "course", labelKey: "helpdesk_category.course" },
        { value: "other", labelKey: "helpdesk_category.other" },
      ],
      loading: false,
      searchQuery: "",
      selectedCategory: "",
      statusFilter: "",
      expandedFaqId: null,
      currentPage: 1,
      itemsPerPage: 10,
      editingFaq: null,
      showModal: false,
      formData: { category: "", question: "", answer: "", sort_order: 0, is_active: true },
    };
  },
  computed: {
    activeFaqs() { return this.faqs.filter((f) => f.is_active).length; },
    monthlyViews() { return this.faqs.reduce((s, f) => s + (f.views || 0), 0); },
    totalPages() { return Math.ceil(this.filteredFaqs.length / this.itemsPerPage); },
    paginatedFaqs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredFaqs.slice(start, start + this.itemsPerPage);
    },
  },
  mounted() { this.loadFaqs(); },
  methods: {
    async loadFaqs() {
      this.loading = true;
      try {
        const res = await this.$axios({ method: "get", url: "/helpdesk/faqs", loadingBar: true });
        this.faqs = res.data.faqs || [];
        this.filterFaqs();
      } catch (error) {
        console.error("FAQ 로딩 실패:", error);
        this.$swal({ icon: "error", title: "로딩 실패", text: "FAQ 목록을 불러오는데 실패했습니다." });
      } finally {
        this.loading = false;
      }
    },
    filterFaqs() {
      let f = [...this.faqs];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        f = f.filter((x) => x.question.toLowerCase().includes(q) || x.answer.toLowerCase().includes(q));
      }
      if (this.selectedCategory) f = f.filter((x) => x.category === this.selectedCategory);
      if (this.statusFilter === "active") f = f.filter((x) => x.is_active);
      else if (this.statusFilter === "inactive") f = f.filter((x) => !x.is_active);
      f.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      this.filteredFaqs = f;
      this.currentPage = 1;
    },
    toggleAccordion(id) { this.expandedFaqId = this.expandedFaqId === id ? null : id; },
    openCreateModal() {
      this.editingFaq = null;
      this.formData = { category: "", question: "", answer: "", sort_order: 0, is_active: true };
      this.showModal = true;
    },
    openEditModal(faq) {
      this.editingFaq = faq;
      this.formData = {
        category: faq.category, question: faq.question, answer: faq.answer,
        sort_order: faq.sort_order || 0, is_active: faq.is_active,
      };
      this.showModal = true;
    },
    async saveFaq() {
      try {
        if (this.editingFaq) {
          await this.$axios({
            method: "put",
            url: `/helpdesk/faqs/${this.editingFaq.id}`,
            data: this.formData, loadingBar: true,
          });
          this.$swal({ icon: "success", title: "수정 완료", text: "FAQ가 성공적으로 수정되었습니다." });
        } else {
          await this.$axios({ method: "post", url: "/helpdesk/faqs", data: this.formData, loadingBar: true });
          this.$swal({ icon: "success", title: "등록 완료", text: "FAQ가 성공적으로 등록되었습니다." });
        }
        this.showModal = false;
        this.loadFaqs();
      } catch (error) {
        console.error("FAQ 저장 실패:", error);
        this.$swal({ icon: "error", title: "저장 실패", text: "FAQ 저장에 실패했습니다." });
      }
    },
    async deleteFaq(faqId) {
      const result = await Swal.fire({
        icon: "warning", title: "삭제 확인", text: "정말로 이 FAQ를 삭제하시겠습니까?",
        showCancelButton: true, confirmButtonText: "삭제", cancelButtonText: "취소",
      });
      if (result.isConfirmed) {
        try {
          await this.$axios({ method: "delete", url: `/helpdesk/faqs/${faqId}`, loadingBar: true });
          this.$swal({ icon: "success", title: "삭제 완료", text: "FAQ가 성공적으로 삭제되었습니다." });
          this.loadFaqs();
        } catch (error) {
          console.error("FAQ 삭제 실패:", error);
          this.$swal({ icon: "error", title: "삭제 실패", text: "FAQ 삭제에 실패했습니다." });
        }
      }
    },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });
    },
    getCategoryLabel(v) {
      const c = this.categories.find((x) => x.value === v);
      return c ? this.$t(c.labelKey) : v;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.faq-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__filters {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 14px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  // ── FAQ 아코디언 ──
  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    transition: border-color $p-dur $p-ease;

    &.is-open {
      border-color: $p-point-deep;
    }
  }

  &__toggle {
    width: 100%;
    background: transparent;
    border: 0;
    padding: 12px 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
    font-family: $p-font-body;
    color: $p-ink;
    transition: background $p-dur $p-ease;

    &:hover { background: $p-panel-alt; }
    &.is-inactive { opacity: 0.55; }

    .faq-page__item.is-open & {
      background: $p-panel-alt;
      border-bottom: 1px solid $p-line;
    }
  }

  &__question {
    flex: 1;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }

  &__views {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-soft;
    font-family: $p-font-mono;

    > i { font-size: 13px !important; }
  }

  &__chev {
    color: $p-ink-soft;
    font-size: 18px !important;
  }

  &__body {
    padding: 16px 18px;
    background: $p-bg;
  }

  &__answer {
    font-size: 13px;
    line-height: 1.7;
    color: $p-ink;
    white-space: pre-line;
    margin-bottom: 14px;

    :deep(p) { margin: 0 0 8px; &:last-child { margin-bottom: 0; } }
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 12px;
    border-top: 1px solid $p-line-soft;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__updated {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 13px !important; }
  }

  &__actions {
    display: inline-flex;
    gap: 6px;
  }

  // ── 모달 ──
  &__modal-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__modal-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__form-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 14px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 16px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__switch-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;

    > i {
      font-size: 16px !important;
      color: $p-point-deep;
    }
  }

  &__switch-sub {
    margin-top: 2px;
    font-size: 11px;
    color: $p-ink-soft;
  }
}
</style>
